import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useModal } from "../contexts";
import { Template } from "../interfaces";
import { Link, useHistory } from "react-router-dom";
import LoadingModal from "./LoadingModal";
import { OAuthError, postData } from "../backend";
import OAuthModalHubspot from "./OAuthModalHubspot";
import ErrorModal from "./ErrorModal";
import OAuthModal from "./OAuthModal";
import { BLOCK_ICON_MAP, BLOCK_NAME_MAP } from "../utils";
import { ChevronRightIcon } from "@heroicons/react/outline";

interface Props {
  template: Template;
  staticWidth?: boolean;
}

function TemplateListCard(props: Props) {
  const { template } = props;
  const { openModal, closeModal } = useModal();
  const history = useHistory();

  const { t } = useTranslation();

  async function handleCreateWorkflow() {
    openModal(<LoadingModal title={t("Creating Workflow from Template...")} subtitle={t("This may take some time as we create the template documents in your Google Drive.")} />);

    try {
      const workflow = await postData(`/templates/${template!.id}/duplicate/`);

      history.push(`/w/${workflow.id}?tour`);
      closeModal();

    } catch (error) {
      if (error instanceof OAuthError)
        if (error.oauthUrl.includes("app.hubspot"))
          openModal(<OAuthModalHubspot oauthUrl={error.oauthUrl} onClose={handleCreateWorkflow} />);
        else
          openModal(<OAuthModal oauthUrl={error.oauthUrl} onClose={handleCreateWorkflow} />)
      else
        openModal(<ErrorModal details={error} />)
    }
  }

  return (
    <Link to={`/templates/${template.id}`}
      className={`group relative flex flex-col bg-white border border-grey-200 rounded-lg overflow-hidden p-4 flex-shrink-0 ${props.staticWidth ? 'w-full' : 'w-full md:w-1/2 lg:w-1/3'}`}
    >
      {/* Media block - responsive with locked aspect ratio (328:180) */}
      <div className="relative w-full min-w-0 overflow-hidden rounded-md bg-gray-100 flex-shrink-0" style={{ aspectRatio: '328 / 180' }}>
        <img
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform"
          src={template.image}
          alt={template.name}
        />
      </div>

      {/* Text container */}
      <div className="w-full p-2 pt-6 flex flex-col gap-4 h-full">
        {/* Tags row */}
        <div className="flex items-center gap-1 w-full overflow-x-scroll scrollbar-hidden">
          {["previewSourceType", "previewDocumentType"].map((key) => {
            const type = template.workflow?.[key as "previewSourceType" | "previewDocumentType"];
            if (!type) return null;
            return (
              <Fragment key={key}>
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-nowrap flex-shrink-0">
                  <img className="w-4 h-4" src={BLOCK_ICON_MAP[type]} alt={type} />
                  <span className="text-xs text-gray-600 capitalize -mb-0.5">{BLOCK_NAME_MAP[type]}</span>
                </div>
                <ChevronRightIcon className="w-4 h-4 last:invisible flex-shrink-0 text-gray-400" />
              </Fragment>
            );
          })}
          {template.workflow.hasSignatureRequest &&
            <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1 text-nowrap flex-shrink-0">
              <img className="w-4 h-4" src={BLOCK_ICON_MAP["SIGNATURE_REQUEST"]} alt={"SIGNATURE_REQUEST"} />
              <span className="text-xs text-gray-600 capitalize -mb-0.5">{t("Request Signatures")}</span>
            </div>
          }
        </div>

        {/* Text content */}
        <div className="flex flex-col gap-1">
          <h4 className="font-gilroy font-semibold text-[18px] leading-[24px] text-black">
            {template.name}
          </h4>
          <p className="font-gilroy text-xs leading-[14px] text-gray-600 min-h-[28px] overflow-hidden text-ellipsis">
            {template.shortDescription}
          </p>
        </div>

        {/* Split buttons */}
        <div className="flex gap-1.5 w-full flex-col md:flex-row mt-auto">
          <button
            onClick={e => { e.preventDefault(); handleCreateWorkflow() }}
            className="basis-0 grow h-[32px] rounded-lg text-blue hover:bg-blue hover:text-white border border-blue text-[12px] leading-[12px] flex items-center justify-center cursor-pointer"
          >
            {t("Use template")}
          </button>
          <Link
            to={`/templates/${template.id}`}
            className="hover:bg-gray-50 basis-0 grow h-[32px] rounded-lg border border-[#76777c] text-[#76777c] text-[12px] leading-[12px] flex items-center justify-center bg-white cursor-pointer"
          >
            {t("Learn more")}
          </Link>
        </div>
      </div>
    </Link>
  );
}

export default TemplateListCard;

